import { MessageType } from "../../../types/conversationInbox";
import { baseUrl as inboxApiBaseUrl } from "../../../api/_skedInboxApi";
import { getCachedToken } from "../../../api/_tokenRefresh";
import {
  getCachedResourceToken,
  getResourceAuthMode,
} from "../../../api/resourceToken";

import type { InboxV2MessageAttachment } from "../../types";
import type {
  InboxV2RawAttachment,
  InboxV2RawMessage,
  InboxV2RawTiktokMessage,
} from "../real-api/types";

export function isEmptyPlatformAttachmentMessage(message: InboxV2RawMessage) {
  return (
    (message.type === MessageType.fbMessage ||
      message.type === MessageType.igMessage ||
      message.type === MessageType.ttMessage) &&
    !(message.text ?? "").trim()
  );
}

export function mapMessageStory(
  message: InboxV2RawMessage
): InboxV2MessageAttachment | undefined {
  if (!("story" in message) || !message.story?.url) {
    return undefined;
  }

  return {
    type: mapAttachmentType(message.story.type),
    title: "Instagram story",
    url: message.story.url,
  };
}

export function mapMessageAttachments(
  message: InboxV2RawMessage
): InboxV2MessageAttachment[] {
  const attachments: InboxV2MessageAttachment[] = [];

  if (message.type === MessageType.ttMessage) {
    if (message.image) {
      attachments.push({
        type: "image",
        url: resolveTiktokMessageMediaUrl(message),
      });
    }
    if (message.video) {
      attachments.push({
        type: "video",
        url: resolveTiktokMessageMediaUrl(message),
      });
    }
    if (message.sharePost?.embed_url) {
      attachments.push({ type: "post", url: message.sharePost.embed_url });
    }
  }

  if ("attachment" in message && message.attachment) {
    const mediaImageUrl = message.attachment.media?.image?.src;
    const mediaSourceUrl = message.attachment.media?.source;

    if (mediaImageUrl) {
      attachments.push({ type: "image", url: mediaImageUrl });
    } else if (mediaSourceUrl) {
      attachments.push({
        type: mapAttachmentType(message.attachment.type),
        url: mediaSourceUrl,
      });
    } else if (message.attachment.url) {
      attachments.push({
        type: mapAttachmentType(message.attachment.type),
        url: message.attachment.url,
      });
    } else {
      attachments.push({
        type: mapAttachmentType(message.attachment.type),
      });
    }
  }

  const directAttachments =
    "attachments" in message ? message.attachments : undefined;
  for (const attachment of directAttachments ?? []) {
    attachments.push(mapInboxV2DirectAttachment(attachment));
  }

  for (const attachment of getLinkedInCommentContentAttachments(message)) {
    attachments.push(attachment);
  }

  return deduplicateStickerAttachments(attachments);
}

function resolveTiktokMessageMediaUrl(message: InboxV2RawTiktokMessage) {
  if (!message.mediaUrl) {
    return undefined;
  }

  const mediaUrl = new URL(
    `/inbox-v2/messages/${message.id}/media`,
    inboxApiBaseUrl
  );
  const authMode = getResourceAuthMode();
  const token =
    authMode === "better-auth"
      ? getCachedResourceToken("inbox")
      : authMode === "legacy"
        ? getCachedToken()
        : undefined;
  if (!token) {
    return undefined;
  }

  mediaUrl.searchParams.set("url", message.mediaUrl);
  mediaUrl.searchParams.set("token", token);

  return mediaUrl.toString();
}

function deduplicateStickerAttachments(
  attachments: InboxV2MessageAttachment[]
): InboxV2MessageAttachment[] {
  const stickerUrls = new Set(
    attachments.flatMap((attachment) =>
      attachment.type === "sticker" && attachment.url ? [attachment.url] : []
    )
  );
  const seenStickerUrls = new Set<string>();

  return attachments.filter((attachment) => {
    if (!attachment.url) {
      return true;
    }

    if (attachment.type !== "sticker") {
      return !stickerUrls.has(attachment.url);
    }

    if (seenStickerUrls.has(attachment.url)) {
      return false;
    }

    seenStickerUrls.add(attachment.url);
    return true;
  });
}

function mapAttachmentType(
  type: string | null | undefined
): InboxV2MessageAttachment["type"] {
  if (!type) {
    return "unknown";
  }

  const normalizedType = type.toLowerCase();

  if (normalizedType === "audio") {
    return "audio";
  }

  if (normalizedType === "sticker") {
    return "sticker";
  }

  if (normalizedType === "share") {
    return "share";
  }

  if (normalizedType === "fallback") {
    return "fallback";
  }

  if (normalizedType === "ig_reel" || normalizedType === "reel") {
    return "reel";
  }

  if (normalizedType === "ig_post" || normalizedType === "post") {
    return "post";
  }

  if (normalizedType === "ig_story" || normalizedType === "story_mention") {
    return "story";
  }

  if (normalizedType === "appointment_booking") {
    return "appointment-booking";
  }

  if (normalizedType === "template") {
    return "template-product";
  }

  if (normalizedType === "ephemeral") {
    return "ephemeral";
  }

  if (normalizedType.includes("image") || normalizedType.includes("photo")) {
    return "image";
  }

  if (
    normalizedType.includes("video") ||
    normalizedType.includes("animated_image")
  ) {
    return "video";
  }

  if (normalizedType.includes("file")) {
    return "file";
  }

  return "unknown";
}

function mapInboxV2DirectAttachment(
  attachment: InboxV2RawAttachment
): InboxV2MessageAttachment {
  switch (attachment.type) {
    case "ig_story":
      return {
        type: "story",
        storyMediaId: attachment.payload?.story_media_id,
        title: "Instagram story",
        url: attachment.payload?.story_media_url,
      };
    case "story_mention":
      return {
        type: "story",
        title: "Instagram story",
        url: attachment.payload?.url,
      };
    case "ig_reel":
    case "reel":
      return {
        reelVideoId: attachment.payload?.reel_video_id,
        type: "reel",
        title: attachment.payload?.title || "Instagram reel",
        url: attachment.payload?.url,
      };
    case "ig_post":
      return {
        postMediaId: attachment.payload?.ig_post_media_id,
        type: "post",
        title: attachment.payload?.title || "Shared post",
        url: attachment.payload?.url,
      };
    case "post":
      return {
        postId: attachment.payload?.id,
        type: "post",
        title: attachment.payload?.title || "Shared post",
        url: attachment.payload?.url,
      };
    case "audio":
      return {
        type: "audio",
        title: "Audio clip",
        url: attachment.payload?.url,
      };
    case "share":
      return {
        type: "share",
        title: "Shared content",
        url: attachment.payload?.url,
      };
    case "fallback":
      return {
        type: "fallback",
        title: attachment.payload?.title || "Shared link",
        url: attachment.payload?.url,
      };
    case "appointment_booking":
      return {
        type: "appointment-booking",
        bookingEndTime: attachment.payload?.end_time,
        bookingId: attachment.payload?.booking_id,
        bookingStartTime: attachment.payload?.start_time,
        bookingStatus: attachment.payload?.status,
        bookingTimezone: attachment.payload?.timezone,
        title: "Appointment booking",
      };
    case "template": {
      const product = attachment.payload?.product?.elements?.[0];

      return {
        type: "template-product",
        imageUrl: product?.image_url,
        productElementId: product?.id,
        retailerId: product?.retailer_id,
        subtitle: product?.subtitle,
        title: product?.title || "Product referral",
      };
    }
    case "ephemeral":
      return {
        type: "ephemeral",
        title: "Ephemeral attachment",
      };
    case "image":
    case "video":
    case "file":
    case "sticker":
    case "unsupported_type":
    default:
      return {
        type: mapAttachmentType(attachment.type),
        title: undefined,
        url: attachment.payload?.url,
      };
  }
}

function getLinkedInCommentContentAttachments(
  message: InboxV2RawMessage
): InboxV2MessageAttachment[] {
  if (
    message.type !== MessageType.liComment &&
    message.type !== MessageType.liThreadedComment
  ) {
    return [];
  }

  const content = message.content;

  if (!Array.isArray(content)) {
    return [];
  }

  return content.map((element) => ({
    type: mapAttachmentType(element.type),
    url: element.url,
  }));
}
